// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {DragDropContext} from 'react-beautiful-dnd';
import styled from 'styled-components';
import {cloneDeep} from 'lodash';

// Material UI
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';

// Components
import StorefrontList from './storefront-list';

// Styled Components
const Container = styled.div`
  width: '100%';
`;
const DragDropContainer = styled.div`
  box-sizing: border-box;
  margin-top: 2rem;
  max-height: 40rem;
  padding: 16px;
  width: 85%;

  /* flexbox */
  align-items: flex-start;
  display: flex;
  justify-content: center;
`;
const VerticalScrollContainer = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: flex-start;
  max-height: 38rem;
  overflow-y: overlay;
`;

const Alert = props => <MuiAlert elevation={6} variant="filled" {...props} />;

const sortByKey = (array, key) => {
  if (array) {
    if (array.length > 1) {
      array.sort((a, b) => (a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0));
    }
    return array;
  }
};

const validateData = data =>
  data === undefined ? false : !(data.limit === undefined || data.active === undefined || data.inactive === undefined);

const parseData = data => {
  const dataCopy = cloneDeep(data);
  let activeData = [];
  let inactiveData = [];

  if (validateData(dataCopy)) {
    activeData = sortByKey(dataCopy.active, 'rank');
    inactiveData = sortByKey(dataCopy.inactive, 'name');

    if (activeData) {
      activeData.forEach(obj => {
        obj.id = String(obj.id);
      });
    }

    if (inactiveData) {
      inactiveData.forEach(obj => {
        obj.id = String(obj.id);
      });
    }
  }

  return {
    inactive: {
      id: 'inactive',
      title: 'Inactive Storefronts',
      items: inactiveData,
    },
    active: {
      id: 'active',
      title: 'Active Storefronts',
      items: activeData,
    },
  };
};

const MultiContainerDragNDrop = ({data, onClick, dropDisabled}) => {
  const parsedData = parseData(data);
  const [maxActive, setMaxActive] = useState(data.limit);
  const [storefrontStates, setStorefrontStates] = useState(parsedData);
  const [sourceContainer, setSourceContainer] = useState(null);
  const [open, setOpen] = useState(storefrontStates.active.items.length >= maxActive);

  useEffect(() => {
    setStorefrontStates(parseData(data));
    setMaxActive(data.limit);
  }, [data, maxActive]);

  useEffect(() => {
    setOpen(storefrontStates.active.items.length >= maxActive);
  }, [maxActive, storefrontStates.active.items.length]);

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const reorderContainer = (droppableId, itemList) => {
    if (droppableId === 'active') {
      itemList.forEach((item, index) => {
        item.active = true;
        item.order = index + 1;
      });
    } else {
      itemList.forEach(item => {
        item.active = false;
        item.order = 0;
      });
      sortByKey(itemList, 'name');
    }
    return itemList;
  };

  const onDragStart = start => {
    setSourceContainer(start.source.droppableId);
  };

  const onDragEnd = result => {
    const {destination, source} = result;
    setSourceContainer(null);

    if (storefrontStates.active.items.length >= maxActive && sourceContainer) {
      setOpen(true);
    }

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const start = storefrontStates[source.droppableId];
    const finish = storefrontStates[destination.droppableId];

    if (start === finish) {
      const newItems = start.items;
      const reorderedItem = newItems.splice(source.index, 1)[0];

      newItems.splice(destination.index, 0, reorderedItem);

      const newContainer = {
        ...start,
        items: reorderContainer(source.droppableId, newItems),
      };

      setStorefrontStates({
        ...storefrontStates,
        [newContainer.id]: newContainer,
      });
      return;
    }

    // Moving from one container to another
    const startItems = start.items;
    const transitionedItem = startItems.splice(source.index, 1)[0];

    const newStart = {
      ...start,
      items: reorderContainer(start.id, startItems),
    };

    const finishItems = finish.items;
    finishItems.splice(destination.index, 0, transitionedItem);
    const newFinish = {
      ...finish,
      items: reorderContainer(finish.id, finishItems),
    };

    setStorefrontStates({
      ...storefrontStates,
      [newStart.id]: newStart,
      [newFinish.id]: newFinish,
    });
  };

  const {length: activeListSize} = storefrontStates.active.items;
  const {active} = storefrontStates;
  const {inactive} = storefrontStates;

  return (
    <Container>
      <DragDropContext isDragDisabled={dropDisabled} onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <DragDropContainer>
          <VerticalScrollContainer>
            <StorefrontList container={inactive} items={inactive.items} key={inactive.id} onClick={onClick} />
          </VerticalScrollContainer>
          <VerticalScrollContainer>
            <StorefrontList
              active={true}
              container={active}
              isDropDisabled={Boolean(active.items.length >= maxActive && sourceContainer !== active.id)}
              items={active.items}
              key={active.id}
              maxActive={maxActive}
              onClick={onClick}
            />
          </VerticalScrollContainer>
        </DragDropContainer>
      </DragDropContext>
      {activeListSize >= maxActive ? (
        <Snackbar autoHideDuration={6000} open={open} onClose={handleClose}>
          {activeListSize > maxActive ? (
            <Alert severity="error" onClose={handleClose}>
              There are too many active storefronts ({maxActive} max). Please resolve this immediately.
            </Alert>
          ) : (
            <Alert severity="info" onClose={handleClose}>
              The active storefronts limit is {maxActive}. Remove an active storefront before adding a new one.
            </Alert>
          )}
        </Snackbar>
      ) : null}
    </Container>
  );
};

MultiContainerDragNDrop.propTypes = {
  data: PropTypes.object,
  dropDisabled: PropTypes.bool,
  onClick: PropTypes.func,
};

export default MultiContainerDragNDrop;
